/**
 * Helpers de conta bancária compartilhados pela Movimentação Financeira.
 *
 * PORQUÊ existe: a MESMA conta real aparece duplicada quando o cliente migra de
 * OFX para Open Finance — o extrato OFX embute o dígito verificador no número
 * ("268265674") e o cadastro/Open Finance guarda sem ele ("26826567"). Sem
 * agrupar, o seletor mostra a conta duas vezes e o filtro só pega metade dos
 * lançamentos.
 *
 * Fica num módulo próprio (e não no index.jsx) porque o modal de lançamento
 * também precisa — importar do index criaria dependência circular.
 */

export const soDigitos = (v) => String(v ?? '').replace(/\D/g, '');

/**
 * Agência normalizada para comparação: só dígitos e SEM zeros à esquerda.
 * PORQUÊ: a mesma agência vem formatada diferente conforme a origem — o OFX
 * grava "0001", o cadastro/Open Finance grava "1" (e às vezes com o DV colado,
 * ex. "0001-2"). Sem normalizar, "0001" !== "1" travava o agrupamento por DV
 * e a MESMA conta aparecia duas vezes no seletor.
 */
export const normAgencia = (v) => {
    const d = soDigitos(v);
    return d ? String(Number(d)) : '';
};

/**
 * Identidade do banco, tolerante às três formas que o dado aparece:
 *   • código com zero à esquerda ("0403" e "403" são o MESMO banco);
 *   • nome ausente, gravado como o próprio código ("0403" no lugar de
 *     "Banco Cora") — legado de antes de termos a lista FEBRABAN;
 *   • só nome, sem código.
 * Devolve { cod, nome } já normalizados; string vazia = não informado.
 */
function identidadeBanco(acc) {
    const nomeBruto = String(acc?.banco?.nome ?? acc?.nome ?? '').trim();
    const codBruto = soDigitos(acc?.banco?.codigoFEBRABAN ?? acc?.banco_codigo ?? '');
    // Nome puramente numérico É o código disfarçado — trata como código.
    const codDoNome = /^\d+$/.test(nomeBruto) ? soDigitos(nomeBruto) : '';
    const digitos = codBruto || codDoNome;
    const cod = digitos ? String(Number(digitos)) : '';   // tira zeros à esquerda
    return {
        cod: cod && cod !== '0' ? cod : '',
        nome: codDoNome ? '' : nomeBruto.toUpperCase(),
    };
}

/** Mesmo banco? Compara por código; sem código, por nome. Se um dos lados não
 *  tem identidade nenhuma, não bloqueia o agrupamento (a conta decide). */
function mesmoBanco(a, b) {
    if (a.cod && b.cod) return a.cod === b.cod;
    if (a.nome && b.nome) return a.nome === b.nome;
    const aVazio = !a.cod && !a.nome;
    const bVazio = !b.cod && !b.nome;
    return aVazio || bVazio;
}

/** Mesma agência? Compara SEM zeros à esquerda; tolera 1 dígito a mais (DV/
 *  formato) tal como fazemos com a conta. Se um dos lados não informa, não
 *  bloqueia (a conta decide). */
function mesmaAgencia(a, b) {
    const na = normAgencia(a);
    const nb = normAgencia(b);
    if (!na || !nb) return true;
    if (na === nb) return true;
    const [curta, longa] = na.length <= nb.length ? [na, nb] : [nb, na];
    return longa.startsWith(curta) && longa.length - curta.length <= 1;
}

/**
 * Agrupa contas que são a MESMA conta real cadastrada de formas diferentes.
 *
 * Só agrupa quando uma é PREFIXO da outra com no máximo 1 dígito a mais (o DV),
 * mesmo banco e mesma agência — nunca junta contas de fato diferentes.
 * Os ids originais ficam em `_ids` (o primeiro é o canônico, usado ao salvar).
 */
export function agruparContasPorDV(contas) {
    const grupos = [];
    // Menor primeiro: a versão SEM o DV vira a âncora (base) do grupo.
    const ordenadas = [...(contas || [])].sort(
        (a, b) => soDigitos(a?.conta).length - soDigitos(b?.conta).length
    );

    ordenadas.forEach((acc) => {
        const cc = soDigitos(acc?.conta);
        const ag = soDigitos(acc?.agencia);
        const ident = identidadeBanco(acc);
        // `x.base &&` evita que conta sem dígitos (base '') engula todas.
        const g = grupos.find((x) =>
            mesmoBanco(x.ident, ident) &&
            mesmaAgencia(x.ag, ag) &&
            x.base &&
            cc.startsWith(x.base) &&
            cc.length - x.base.length <= 1
        );
        if (g) {
            g.ids.push(acc?.id);
            g.variantes.add(cc);
            if (cc.length === g.base.length + 1) g.dv = cc.slice(-1);
            // Prefere como referência a conta que tem NOME de verdade — assim o
            // rótulo vira "Banco Cora" e não "0403".
            if (!g.ident.nome && ident.nome) { g.ident = ident; g.ref = acc }
            if (!g.ag && ag) g.ag = ag;
            return;
        }
        grupos.push({
            base: cc, ag, ident, dv: '',
            ids: [acc?.id], variantes: new Set([cc]), ref: acc,
        });
    });

    return grupos.map((g) => ({
        ...g.ref,
        // Mantém o shape que os seletores já consomem (id / banco.nome / conta).
        // `id` aqui é o id REAL canônico — assim salvar continua funcionando.
        id: g.ids[0],
        banco: g.ref?.banco || { nome: g.ident.nome || g.ident.cod || 'Banco' },
        conta: g.dv ? `${g.base}-${g.dv}` : g.base,
        agencia: g.ref?.agencia,
        _ids: g.ids,
        _base: g.base,
        _ag: g.ag,
        _variantes: Array.from(g.variantes),
    }));
}

/** True se a conta informada pertence ao grupo (com ou sem DV). */
export function contaBateNoGrupo(contaLancamento, grupo) {
    const cc = soDigitos(contaLancamento);
    if (!cc || !grupo) return false;
    if ((grupo._variantes || []).includes(cc)) return true;
    const base = grupo._base;
    return !!base && cc.startsWith(base) && cc.length - base.length <= 1;
}

/**
 * Descobre qual opção do seletor corresponde à conta de um lançamento.
 *
 * O Pantheon devolve a conta do lançamento com agência/número mas SEM id (o
 * converter do portal não preenche `contaBancaria.id`), então casar só por id
 * falhava e o modal caía sempre em "000 - Caixa". Aqui tentamos, nesta ordem:
 *   1. id real (quando existe e bate com algum id do grupo);
 *   2. agência + número da conta (com ou sem DV).
 * Retorna o id canônico do grupo, ou "0" (Caixa) quando não há conta.
 */
export function resolverContaSelecionada(contaBancaria, gruposContas) {
    if (!contaBancaria) return '0';

    const obj = typeof contaBancaria === 'object' ? contaBancaria : null;
    const idBruto = obj ? obj.id : contaBancaria;

    // 1) Por id real
    if (idBruto && String(idBruto) !== '0') {
        const porId = (gruposContas || []).find((g) =>
            (g._ids || []).some((i) => String(i) === String(idBruto))
        );
        if (porId) return String(porId.id);
    }

    // 2) Por agência + conta (é o que o Pantheon manda)
    const cc = soDigitos(obj?.conta);
    if (cc) {
        const ag = normAgencia(obj?.agencia);
        const porConta = (gruposContas || []).find((g) => {
            if (!contaBateNoGrupo(cc, g)) return false;
            const agGrupo = normAgencia(g._ag);
            return !ag || !agGrupo || ag === agGrupo;
        });
        if (porConta) return String(porConta.id);
    }

    // Sem conta bancária identificada → Caixa
    return idBruto ? String(idBruto) : '0';
}
